import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Card,
  Row,
  Col,
  Typography,
  Transfer,
  Tag,
  Avatar,
  Descriptions,
  Badge,
  Input,
  Select
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TransferProps } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  User,
  Role,
  AssignRolesToUserRequest,
  UserRolesResponse
} from '../../types';
import { userApi, roleApi } from '../../services/api';
import { usePermissionCheck, PermissionGuard, usePermissions } from '../../context/PermissionContext';
import { requestDeduplication } from '../../utils/requestDeduplication';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

interface TransferItem {
  key: string;
  title: string;
  description?: string;
  disabled?: boolean;
}

const UserRoleManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [roleLoading, setRoleLoading] = useState<boolean>(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');

  const { loading: permissionLoading } = usePermissions();
  const {
    canViewUsers,
    canAssignRoles
  } = usePermissionCheck();

  // 获取用户列表
  const fetchUsers = async (
    page = currentPage,
    size = pageSize,
    search = searchText,
    role = filterRole
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: size,
        search,
        role,
      };
      const response = await userApi.getUsers(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取所有角色
  const fetchAllRoles = async () => {
    try {
      // 使用请求去重，避免重复调用
      const response = await requestDeduplication.request(
        'getAllRoles',
        () => roleApi.getAllRoles()
      );
      setAllRoles(response.data.roles || []);
    } catch (error: any) {
      console.error('获取角色列表失败:', error);
    }
  };

  // 获取用户角色
  const fetchUserRoles = async (userId: number) => {
    try {
      setRoleLoading(true);
      const response = await roleApi.getUserRoles(userId);
      setUserRoles(response.data.roles || []);
    } catch (error: any) {
      console.error('获取用户角色失败:', error);
      message.error('获取用户角色失败');
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    if (!permissionLoading && canViewUsers()) {
      fetchUsers(currentPage, pageSize, searchText, filterRole);
      fetchAllRoles();
    }
  }, [permissionLoading, currentPage, pageSize, searchText, filterRole]);

  // 角色分配
  const handleRoleAssignment = (user: User) => {
    setSelectedUser(user);
    fetchUserRoles(user.id);
    setIsRoleModalVisible(true);
  };

  // 保存角色分配
  const handleSaveRoles = async (targetKeys: React.Key[]) => {
    if (!selectedUser) return;
    
    try {
      const roleIds = targetKeys.map(key => parseInt(key.toString()));
      await roleApi.assignRolesToUser(selectedUser.id, { role_ids: roleIds });
      message.success('角色分配成功');
      setIsRoleModalVisible(false);
      setSelectedUser(null);
      setUserRoles([]);
      fetchUsers(currentPage, pageSize, searchText, filterRole); // 刷新用户列表以更新显示
    } catch (error: any) {
      console.error('分配角色失败:', error);
      message.error(error.response?.data?.message || '分配角色失败');
    }
  };

  // 获取用户角色标签
  const getUserRolesTags = (user: User) => {
    // 这里需要从用户数据中获取角色信息
    // 暂时返回管理员状态
    return user.is_admin ? [<Tag key="admin" color="red">管理员</Tag>] : [];
  };

  // 表格列定义
  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户信息',
      key: 'userInfo',
      width: 250,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />} 
            style={{ marginRight: 12, backgroundColor: '#1890ff' }}
          >
            {record.name.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {record.username} • {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      render: (balance: number) => {
        const balanceValue = balance ?? 0;
        return (
          <span style={{ color: balanceValue > 0 ? '#52c41a' : '#f5222d' }}>
            ¥{balanceValue.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: '角色',
      key: 'roles',
      width: 200,
      render: (_, record) => (
        <div>
          {record.is_admin ? (
            <Tag color="red">超级管理员</Tag>
          ) : (
            <Tag color="default">普通用户</Tag>
          )}
        </div>
      ),
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Badge 
          status={record.is_admin ? 'error' : 'success'} 
          text={record.is_admin ? '管理员' : '普通用户'} 
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <PermissionGuard permission="user.assign_role">
            <Button
              type="primary"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => handleRoleAssignment(record)}
            >
              分配角色
            </Button>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  // 权限检查失败时的展示
  if (!canViewUsers()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看用户角色管理的权限</p>
        </div>
      </Card>
    );
  }

  // 准备角色穿梭框数据
  const roleTransferData: TransferItem[] = allRoles.map(role => ({
    key: role.id.toString(),
    title: role.name,
    description: `${role.code} - ${role.description || ''}`,
    disabled: role.is_system && role.code === 'super_admin', // 禁止分配超级管理员角色
  }));

  const roleTargetKeys = userRoles.map(role => role.id.toString());

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>用户角色管理</Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchUsers()}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 搜索和过滤 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="搜索用户名、用户姓名或邮箱"
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={setSearchText}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="筛选角色"
              allowClear
              value={filterRole}
              onChange={setFilterRole}
              style={{ width: '100%' }}
            >
              <Option value="admin">管理员</Option>
              <Option value="user">普通用户</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size || 10);
              fetchUsers(page, size || 10, searchText, filterRole);
            },
          }}
        />

        {/* 角色分配模态框 */}
        <Modal
          title={
            <Space>
              <TeamOutlined />
              <span>角色分配 - {selectedUser?.name}</span>
            </Space>
          }
          open={isRoleModalVisible}
          onCancel={() => {
            setIsRoleModalVisible(false);
            setSelectedUser(null);
            setUserRoles([]);
          }}
          footer={null}
          width={800}
        >
          {selectedUser && (
            <div style={{ marginBottom: 16 }}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="用户ID">
                  {selectedUser.id}
                </Descriptions.Item>
                <Descriptions.Item label="用户名">
                  {selectedUser.name}
                </Descriptions.Item>
                <Descriptions.Item label="用户姓名">
                  {selectedUser.username}
                </Descriptions.Item>
                <Descriptions.Item label="邮箱">
                  {selectedUser.email}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
          
          {roleLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              正在加载角色数据...
            </div>
          ) : (
            <Transfer
              dataSource={roleTransferData}
              targetKeys={roleTargetKeys}
              showSearch
              listStyle={{
                width: 350,
                height: 400,
              }}
              operations={['分配', '移除']}
              titles={['可分配角色', '已分配角色']}
              render={item => (
                <div>
                  <div style={{ fontWeight: 500 }}>{item.title}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{item.description}</div>
                </div>
              )}
              onChange={(targetKeys) => handleSaveRoles(targetKeys)}
              disabled={!canAssignRoles()}
            />
          )}
          
          {!canAssignRoles() && (
            <div style={{ textAlign: 'center', marginTop: 16, color: '#999' }}>
              您没有分配角色的权限
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default UserRoleManagement; 